// Skyward PM C3 — live-status brand block for the top of the side menu
// (spec SWI_PM_C3_Live_Block_Spec_R1.2). Same grid-bubble mark as the C3 loader.
//
// Shows whether this browser is connected live:
//   live         — green dot with a slow steady pulse; bubble + trace pulse once per heartbeat (`beat` changes, every 5 s)
//   connecting   — orange arc runs round the bubble (first load / sign-in)
//   reconnecting — amber arc runs round the bubble, trace dashes move
//   offline      — dashed grey ring, red dot, flat trace, "changes can't be saved"
//
// The component only displays a status. Deciding the status (realtime channel state +
// heartbeat) belongs in a hook — see useConnectionStatus in the spec, §4.

export type LiveStatus = 'live' | 'connecting' | 'reconnecting' | 'offline';

export interface C3LiveBlockProps {
  status: LiveStatus;
  /** Changes (e.g. a counter or timestamp) on every successful heartbeat → one pulse. */
  beat?: number;
  /** Time of the last successful check, shown in the tooltip. */
  lastBeatAt?: Date | null;
  /** Extra text after "Live", e.g. "3 online". */
  detail?: string;
  /** Icon-only version for a collapsed menu or the phone top bar. */
  collapsed?: boolean;
  onClick?: () => void;
}

const TEXT: Record<LiveStatus, string> = {
  live: 'Live',
  connecting: 'Connecting…',
  reconnecting: 'Reconnecting…',
  offline: "Offline · changes paused",
};

function fmtTime(d?: Date | null) {
  if (!d) return '';
  return d.toLocaleTimeString('en-FJ', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

function Mark({ status, beat }: { status: LiveStatus; beat?: number }) {
  return (
    <svg className="c3lb-mark" viewBox="0 0 48 48" width="40" height="40" aria-hidden="true">
      {status === 'live' && <circle key={`p${beat ?? 0}`} className="c3lb-pulse" cx="24" cy="24" r="19" />}
      <line className="c3lb-stub" x1="24" y1="0" x2="24" y2="3.5" />
      <line className="c3lb-stub" x1="24" y1="44.5" x2="24" y2="48" />
      <circle className="c3lb-track" cx="24" cy="24" r="19" />
      <circle className="c3lb-arc" cx="24" cy="24" r="19" />
      <text className="c3lb-c3" x="24" y="28.8" textAnchor="middle">C3</text>
      {status === 'live' && <circle className="c3lb-halo" cx="40" cy="40" r="5.5" />}
      <circle className="c3lb-dot" cx="40" cy="40" r="5.5" />
    </svg>
  );
}

const TRACE = 'M0 7 H88 L91 7 L94 1.5 L98 12.5 L101 4.5 L104 7 H200';

export function C3LiveBlock({ status, beat, lastBeatAt, detail, collapsed, onClick }: C3LiveBlockProps) {
  const label = TEXT[status] + (status === 'live' && detail ? ` · ${detail}` : '');
  const tip = `Skyward PM C3 — ${TEXT[status]}${lastBeatAt ? ` · last check ${fmtTime(lastBeatAt)}` : ''}`;
  const cls = `c3lb c3lb--${status}${collapsed ? ' c3lb--collapsed' : ''}`;
  const inner = (
    <>
      <div className="c3lb-row">
        <Mark status={status} beat={beat} />
        {!collapsed && (
          <div className="c3lb-text">
            <span className="c3lb-eyebrow">Skyward Industries</span>
            <strong className="c3lb-name">PM C3</strong>
            <span className="c3lb-status" role="status" aria-live="polite">{label}</span>
          </div>
        )}
      </div>
      {!collapsed && (
        <svg className="c3lb-trace" viewBox="0 0 200 14" preserveAspectRatio="none" aria-hidden="true">
          <path className="c3lb-base" d={TRACE} />
          {status === 'live' && <path key={`t${beat ?? 0}`} className="c3lb-run" d={TRACE} />}
        </svg>
      )}
      {collapsed && <span className="c3lb-sr">{label}</span>}
    </>
  );
  return onClick ? (
    <button type="button" className={cls} title={tip} onClick={onClick}>{inner}</button>
  ) : (
    <div className={cls} title={tip}>{inner}</div>
  );
}
